import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FunctionalLoader } from 'seagull';
import FeatureFlagsGuard from '~/components/FeatureFlagsGuard/FeatureFlagsGuard';
import ImpersonateWarning from '~/components/ImpersonateWarning/ImpersonateWarning';
import { useStore } from '~/contexts';
import 'seagull/foundations/font-face.scss';
import { useTeamInvites } from '~/hooks/reactQuery/queries/useTeamInvites';
import { useBiometricUserSwitchGuard } from '~/hooks/useBiometricUserSwitchGuard';
import { useFeatureFlagsFetcher } from '~/hooks/useFeatureFlagsFetcher';
import { useKycStatusBinder } from '~/hooks/useKycStatusBinder';
import { useNativePush } from '~/hooks/useNativePush';
import { usePendingContactEmailUpdatesListener } from '~/hooks/usePendingContactEmailUpdatesListener';
import { useRestoredDocumentIdentityGuard } from '~/hooks/useRestoredDocumentIdentityGuard';
import { useSessionTimeout } from '~/hooks/useSessionTimeout';
import { useSyncUserDataBetweenTabs } from '~/hooks/useSyncUserDataBetweenTabs';
import { useLocaleKeys } from '~/i18n';
import { LoginScreen } from '~/pages/unAuthorizedPages';
import { withOptimizedMobileViewport } from '~/providers/MobileViewportWidthProvider';
import { selectors } from '~/store/state-selectors';
import { unauthorizedPages, getAuthorizedPages, preloadBootRouteChunks } from '~/utils/routeConfigs';
import { BeforeAuthFlowDone as BeforeAuthFlowDoneScreen } from './BeforeAuthFlowDone/BeforeAuthFlowDone';
import MainPages from './MainPages';
const BeforeAuthFlowDone = withOptimizedMobileViewport(BeforeAuthFlowDoneScreen);
const LoggedOutComponent = withOptimizedMobileViewport(LoginScreen);
export default function Main() {
    const hasUserInfo = useStore(selectors.user.hasUserInfo);
    const isResetPasswordFlow = useStore(selectors.isResetPasswordFlow);
    const hasAuthToken = useStore((state)=>state.authToken != null);
    const authFlowDone = useStore((state)=>state.authFlowDone);
    const isWaitingForUserInfo = hasAuthToken && !isResetPasswordFlow && !hasUserInfo;
    const authorizedPages = getAuthorizedPages();
    const location = useLocation();
    const [bootPathname] = useState(location.pathname);
    useSyncUserDataBetweenTabs();
    useRestoredDocumentIdentityGuard();
    useSessionTimeout();
    useBiometricUserSwitchGuard();
    useLocaleKeys();
    useKycStatusBinder();
    useNativePush();
    useFeatureFlagsFetcher();
    usePendingContactEmailUpdatesListener();
    useTeamInvites();
    useEffect(()=>{
        if (hasAuthToken) {
            preloadBootRouteChunks(bootPathname).catch(()=>{});
        }
    }, [
        hasAuthToken,
        bootPathname
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ImpersonateWarning, {}),
            /*#__PURE__*/ _jsx(FunctionalLoader, {
                name: "waitingForUserInfo",
                show: isWaitingForUserInfo
            }),
            authFlowDone ? /*#__PURE__*/ _jsx(FeatureFlagsGuard, {
                children: /*#__PURE__*/ _jsx(MainPages, {
                    authorizedPages: authorizedPages,
                    unauthorizedPages: unauthorizedPages,
                    LoggedOutComponent: LoggedOutComponent
                })
            }) : /*#__PURE__*/ _jsx(BeforeAuthFlowDone, {})
        ]
    });
}
